import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import CustomerQuote from "./CustomerQuote";
import MaterialItem from "./QuoteMaterialItem";
import LaborItem from "./QuoteLaborItem";
import DiscountItem from "./QuoteDiscountItem";
import MiscItem from "./QuoteMiscItem";
import Total, { computeTotal } from "./QuoteTotal";
import Status from "./QuoteStatus";
import CustomerTerms from "./CustomerTerms";
import Address from "./Address";
import DocumentRef from "./DocumentRef";
import { deepFreeze, isDue, timestampToDate } from "../util/misc";
import {
  cloneDeep,
  compact,
  defaults,
  difference,
  intersection,
  isEmpty,
  pick,
  uniq,
  zipObject,
} from "lodash";
import { diff } from "deep-object-diff";
import { LOCATION_INFO } from "../constants";
import {
  BillTo,
  BILL_TO_FIELDS,
  BILL_TO_TYPES,
  billToOptionsFor,
  ShipTo,
  SHIP_TO_FIELDS,
  SHIP_TO_TYPES,
} from "./BillTo";
import { scrubCustomerQuote } from "../util/customerQuote.js";

// Re-export BillTo/ShipTo from their own module (extracted to avoid circular deps)
export {
  BillTo,
  BILL_TO_FIELDS,
  BILL_TO_TYPES,
  ShipTo,
  SHIP_TO_FIELDS,
  SHIP_TO_TYPES,
};

//#region Quote

export const QUOTE_SETTINGS = {
  preapproved: false,
  nonfinal: false,
  generalSpecs: "",
  terms: true,
  itemPrices: true,
  totalsFirst: true,
  totalsLast: true,
  removeLineBreaks: false,
  includePDF: false,
};
deepFreeze(QUOTE_SETTINGS);

export const QUOTE_FIELDS = {
  name: "",
  location: null,
  jobsite: null,
  billTo: null,
  shipTo: null,
  deliveryContact: null,
  items: [],
  margin: 25,
  notax: false,
  tax: null,
  terms: null,
  total: null,
  holdScreens: true,
  holdHardware: true,
  requiresPunchOut: true,
  attachments: [],
  // INTERNAL ONLY. Why the Shipping/Delivery Charge was waived or reduced
  // below the standard amount on a quote under the threshold. Deliberately a
  // top-level quote field rather than a field on the shipping item: item
  // objects are copied wholesale into the customer-readable quote document
  // (`toCustomerQuote` scrubs items by denylist), whereas top-level fields
  // are dropped by the CUSTOMER_QUOTE_FIELDS whitelist. Keeping it here makes
  // the privacy structural instead of something a future edit must remember.
  shippingWaiveReason: "",
  // INTERNAL ONLY. Why the paid-in-full protocol was overridden on a cash sale
  // under the threshold -- cash sales under the threshold are supposed to be
  // collected in full before we order, so a salesperson saving one on partial
  // terms has to state a reason. Top-level for the same privacy reason as
  // `shippingWaiveReason`: the CUSTOMER_QUOTE_FIELDS whitelist drops it.
  cashSaleWaiveReason: "",
  importedPdf: null,
  settings: { ...QUOTE_SETTINGS },
  status: {},
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(QUOTE_FIELDS);

export default class Quote extends DataStructure {
  constructor(salesLeadID, id, data = {}) {
    // Backward compatibility
    data = { ...data };
    if (!data.status) {
      data.status = {};
    }
    if (data.sent) {
      if (!data.status.sent) {
        data.status.sent = data.sent;
      }
    }
    if (data.responses) {
      if (!data.status.responses) {
        data.status.responses = data.responses;
      }
    }

    data = sanitize(data, QUOTE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.salesLeadID = salesLeadID;
    this.id = id;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      change: this.canBeChangedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      decline: this.canBeDeclinedBy,
      "view-cost": this.canViewCost,
    });

    // Defaults
    const salesLead = this.salesLead;
    this.data.settings = defaults(this.data.settings, QUOTE_SETTINGS);

    // Jobsite
    if (!this.data.jobsite || !this.jobsite.isValid) {
      if (this.data.shipTo && this.data.shipTo.type === "jobsite") {
        this.data.jobsite = cloneDeep(this.data.shipTo.address);
      } else if (salesLead) {
        this.data.jobsite = cloneDeep(salesLead.data.address);
      }
    }

    // BillTo
    if (salesLead && !this.data.billTo) {
      const options = billToOptionsFor(salesLead.receivedFrom);
      const type = options.company ? "company" : "person";
      if (options[type]) {
        this.data.billTo = { type, address: options[type] };
      }
    }
    this.data.billTo = this.billTo.data;

    // ShipTo
    if (!this.data.shipTo) {
      this.data.shipTo = {
        type: "jobsite",
        address: this.data.jobsite,
      };
    }
    this.data.shipTo = this.shipTo.data;

    // Terms
    this.data.terms = this.terms.data;

    // Total
    if (this.data.total && !("taxPercent" in this.data.total)) {
      this.data.total.taxPercent = this.data.tax / 100;
    }
    // Recalculate tax if the tax percentage has changed (e.g., updated via API)
    // but preserve existing item prices
    if (
      this.data.total &&
      this.data.tax !== null &&
      this.data.total.taxPercent !== this.data.tax / 100
    ) {
      this.data.total = this.computeTotal({
        includeCost: "cost" in this.data.total,
      });
    }
    this.data.total = this.total.data;

    // Status
    this.data.status = this.status.data;

    // Documents
    if (this.data.attachments) {
      this.data.attachments = this.data.attachments.map(
        (d) => new DocumentRef(d).data
      );
    }

    // Set item lines if not set
    if (this.data.items.length && !this.data.items[0].line) {
      this.setItemLines();
    }
  }

  // Permissions

  canBeEditedBy(user) {
    const salesLead = this.salesLead;
    return (
      !this.data.locked &&
      salesLead &&
      !salesLead.data.locked &&
      (user.isAdmin ||
        user.id === this.data.createdBy ||
        user.id === salesLead.data.status.assignedTo ||
        this.isTeamMember(user.id, salesLead.data.status.assignedTo))
    );
  }

  // Check if a user is on the same sales team as the assigned salesperson
  isTeamMember(userID, assignedTo) {
    if (!assignedTo) return false;
    const salesTeams = store.state.main.salesTeams || {};
    for (const team of Object.values(salesTeams)) {
      if (!team.data.active) continue;
      const memberIDs = team.memberIDs || [];
      // Check if both the user and the assignee are on this team
      if (memberIDs.includes(userID) && memberIDs.includes(assignedTo)) {
        return true;
      }
    }
    return false;
  }

  canBeChangedBy(user) {
    const salesLead = this.salesLead;
    return (
      salesLead &&
      salesLead.isSold &&
      salesLead.sale &&
      salesLead.sale.canBeChangedBy(user)
    );
  }

  canBeDeletedBy(user) {
    const salesLead = this.salesLead;
    return (
      !this.data.locked &&
      salesLead &&
      !salesLead.data.locked &&
      (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  canBeDeclinedBy(user) {
    return (
      (user.isAdmin || (user.isEmployee && user.id === this.data.createdBy)) &&
      this.status.sent &&
      this.status.sent.date &&
      !this.status.hasResponse
    );
  }

  canViewCost(user) {
    return user.isEmployee;
  }

  get expired() {
    return this.data.status.sent
      ? isDue(this.data.status.sent.date, -30)
      : false;
  }

  // Database

  get path() {
    return Quote.getPath(this.salesLeadID, this.id);
  }

  static getPath(salesLeadID, id) {
    let path = `sales-leads/${salesLeadID}/quotes`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(quote) {
    return quote.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Quote(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Editing

  getOriginal() {
    return this.data.status.original
      ? store.dispatch("GET_QUOTE", {
          salesLeadID: this.salesLeadID,
          quoteID: this.data.status.original,
        })
      : null;
  }

  getChanges(original, excludeCost = false) {
    const changes = [];
    const originalData = Quote.cleanDocuments(cloneDeep(original.data));
    const newData = Quote.cleanDocuments(cloneDeep(this.data));

    if (excludeCost) {
      originalData.items
        .concat(newData.items)
        .forEach((item) => delete item.cost);
    }

    // Exclude images and profitMode
    delete originalData.profitMode;
    delete newData.profitMode;
    originalData.items
      .concat(newData.items)
      .forEach((item) => delete item.image);

    // Bill To
    let billToBefore = diff(newData.billTo, originalData.billTo);
    if (!isEmpty(billToBefore)) {
      changes.push(
        deepFreeze({
          id: "billTo",
          before: billToBefore,
          after: newData.billTo,
        })
      );
    }

    // Ship To
    let shipToBefore = diff(newData.shipTo, originalData.shipTo);
    if (!isEmpty(shipToBefore)) {
      changes.push(
        deepFreeze({
          id: "shipTo",
          before: shipToBefore,
          after: newData.shipTo,
        })
      );
    }

    // Delivery Contact
    if (newData.deliveryContact !== originalData.deliveryContact) {
      changes.push(
        deepFreeze({
          id: "deliveryContact",
          before: originalData.deliveryContact,
          after: newData.deliveryContact,
        })
      );
    }

    // Attachments
    let attachmentsBefore = diff(newData.attachments, originalData.attachments);
    if (!isEmpty(attachmentsBefore)) {
      changes.push(
        deepFreeze({
          id: "attachments",
          before: attachmentsBefore,
          after: newData.attachments,
        })
      );
    }

    // Terms
    let termsBefore = diff(newData.terms, originalData.terms);
    if (!isEmpty(termsBefore)) {
      changes.push(
        deepFreeze({
          id: "terms",
          before: termsBefore,
          after: newData.terms,
        })
      );
    }

    // Hold Screens
    if (originalData.holdScreens !== newData.holdScreens) {
      changes.push(
        deepFreeze({
          id: "holdScreens",
          before: originalData.holdScreens,
          after: newData.holdScreens,
        })
      );
    }

    // Hold Hardware
    if (originalData.holdHardware !== newData.holdHardware) {
      changes.push(
        deepFreeze({
          id: "holdHardware",
          before: originalData.holdHardware,
          after: newData.holdHardware,
        })
      );
    }

    // Items
    let originalItemIDs = originalData.items.map((i) => i.id);
    let originalItems = zipObject(originalItemIDs, originalData.items);
    let newItemIDs = newData.items.map((i) => i.id);
    let newItems = zipObject(newItemIDs, newData.items);

    // Removed Items
    difference(originalItemIDs, newItemIDs).forEach((id) => {
      changes.push(
        deepFreeze({
          id,
          line: originalItems[id].line,
          before: originalItems[id],
          after: null,
        })
      );
    });

    // Added Items
    difference(newItemIDs, originalItemIDs).forEach((id) => {
      changes.push(
        deepFreeze({
          id,
          line: newItems[id].line,
          before: null,
          after: newItems[id],
        })
      );
    });

    // Modified Items
    intersection(newItemIDs, originalItemIDs).forEach((id) => {
      let after = diff(originalItems[id], newItems[id]);
      if (!isEmpty(after)) {
        changes.push(
          deepFreeze({
            id,
            line: newItems[id].line,
            before: diff(newItems[id], originalItems[id]),
            after,
          })
        );
      }
    });

    // Total
    if (!isEmpty(diff(newData.total, originalData.total))) {
      let totalBefore = originalData.total;
      let totalAfter = newData.total;
      if (excludeCost) {
        delete totalAfter.cost;
        delete totalBefore.cost;
      }
      changes.push(
        deepFreeze({
          id: "total",
          before: totalBefore,
          after: totalAfter,
        })
      );
    }

    return changes;
  }

  setItemLines() {
    this.data.items.forEach((item, i) => {
      item.line = i + 1;
    });
  }

  computeTotal({ items, tax, includeCost, payments, original }) {
    if (items === undefined) {
      items = this.data.items;
    }
    if (tax === undefined) {
      tax = this.data.tax;
    }
    if (payments === undefined) {
      payments = this.data.payments;
    }
    const total = computeTotal({ items, tax, includeCost, payments, original });

    // Preserve any existing taxCounty metadata on total so county label
    // is not lost when totals are recomputed.
    if (this.data.total && this.data.total.taxCounty) {
      total.taxCounty = this.data.total.taxCounty;
    }

    return total;
  }

  static cleanTransientItemImages(data) {
    let removed = 0;
    if (Array.isArray(data.items) && data.items.length) {
      data.items.forEach((item) => {
        if (
          item &&
          item.image &&
          typeof item.image.src === "string" &&
          item.image.src.startsWith("blob:")
        ) {
          item.image = null;
          removed++;
        }
      });
    }
    return removed;
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (removeUnsaved) {
      Quote.cleanTransientItemImages(data);
    }
    if (data.attachments.length) {
      data.attachments = data.attachments.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    return data;
  }

  get isValid() {
    return Boolean(
      this.data.location &&
        this.billTo.isValid &&
        this.shipTo.isValid &&
        this.data.deliveryContact &&
        this.terms.isValid &&
        this.data.items.length > 0 &&
        this.data.tax !== null &&
        this.data.tax !== "" &&
        this.items.every(
          (item) =>
            item.isValid &&
            (!item.data.inventoryID ||
              (item.inventoryItem &&
                item.data.quantity <=
                  item.inventoryItem.getQuantityAvailableForLead(
                    this.salesLeadID
                  )))
        )
    );
  }

  get isEmpty() {
    return !this.id && isEmpty(diff(this.data, new Quote().data));
  }

  isEqual(quote) {
    return (
      this.id === quote.id &&
      this.salesLeadID === quote.salesLeadID &&
      isEmpty(
        diff(
          Quote.cleanDocuments(cloneDeep(this.data)),
          Quote.cleanDocuments(cloneDeep(quote.data))
        )
      )
    );
  }

  get hasMaterialItems() {
    return this.data.items.some((item) => item.itemType === "material");
  }

  // Exporting

  async toCustomerQuote(recipients, settings) {
    let changes = null;
    const original = await this.getOriginal();
    if (original) {
      changes = this.getChanges(original, true);
    }
    const data = {
      ...cloneDeep(this.data),
      settings,
      status: {
        type: "",
        received: {
          recipients,
          date: new Date(),
          nonfinal: settings.nonfinal || false,
        },
        response: null,
      },
      changes,
      salesLeadID: this.salesLeadID,
    };

    // Refresh the jobsite from the lead (project address once sold): the
    // snapshot spread in above was written when the quote was created and
    // goes stale when the address is corrected afterwards.
    const jobsite = this.salesLead && this.salesLead.jobsite;
    if (jobsite && jobsite.isValid) {
      data.jobsite = cloneDeep(jobsite.data);
    }

    // Add contact info
    const salesPerson = this.salesPerson;
    data.contact = {
      name: salesPerson.data.name,
      phones: compact([salesPerson.data.phones[0]]),
      emails: compact([salesPerson.data.emails[0]]),
    };

    const customer = this.billTo.address.contact;
    const customerID = customer.id;
    data.billTo.address.name = customer.name;
    delete data.billTo.address.contact;

    if (data.shipTo.type === "contact") {
      data.shipTo.address.name = customer.name;
      delete data.shipTo.address.contact;
    }

    if (data.deliveryContact) {
      data.deliveryContact = pick(this.deliveryContact.data, [
        "name",
        "emails",
        "phones",
      ]);
    }

    // Remove sensitive or superfluous data: from the items and total, and
    // from every item and total in the change order's changes
    delete data.margin;
    if (settings.nonfinal) {
      data.items.forEach((item) => {
        if (item.itemType === "material") {
          item.description = "";
        }
      });
    }

    return new CustomerQuote(customerID, this.id, scrubCustomerQuote(data));
  }

  // Formatting

  get salesPerson() {
    return this.salesLead ? this.salesLead.status.assignedTo : null;
  }

  get salesLead() {
    return this.salesLeadID
      ? store.state.main.salesLeads[this.salesLeadID] || null
      : null;
  }

  get location() {
    // `|| null` so an unrecognized key degrades to null rather than undefined —
    // consumers like CompanyLocationBadge declare location as a required
    // Object, and undefined trips prop validation. Matches getLocationInfo().
    return this.data.location
      ? LOCATION_INFO[this.data.location] || null
      : null;
  }

  get jobsite() {
    return this.data.jobsite ? new Address(this.data.jobsite) : null;
  }

  get billTo() {
    return this.data.billTo ? new BillTo(this.data.billTo) : new BillTo();
  }

  // Derived live from the company being billed rather than stored, so marking
  // a company wholesale takes effect on quotes that already exist. The sale
  // created from this quote stores the answer (`sale.isWholesale`) because a
  // sale's commission treatment must not change under it after the fact.
  get isWholesale() {
    return this.billTo.isWholesale;
  }

  get shipTo() {
    return this.data.shipTo ? new ShipTo(this.data.shipTo) : new ShipTo();
  }

  get deliveryContact() {
    return this.data.deliveryContact
      ? store.state.main.contacts[this.data.deliveryContact] || null
      : null;
  }

  get total() {
    return new Total(this.data.total);
  }

  // Total after requested changes
  get newTotal() {
    const response = this.status.response;
    if (!response) {
      return null;
    }
    let items = cloneDeep(this.data.items);
    response.changes.forEach((change) => {
      if (change.type === "quantity" && items[change.id - 1]) {
        items[change.id - 1].quantity = change.after;
      }
    });
    items = items.filter(
      (item) => item.itemType !== "material" || item.quantity > 0
    );
    const total = new Total({
      items,
      tax: this.data.tax,
      includeCost: true,
      payments: this.data.total.payments,
    });
    if (this.data.total && this.data.total.taxCounty) {
      total.data.taxCounty = this.data.total.taxCounty;
    }
    return total;
  }

  get sortedCategories() {
    const categories = uniq(
      this.data.items.map((item) => item.category || "Other")
    );
    if (categories.length > 1 || categories[0] !== "Other") {
      return categories;
    }
    return [];
  }

  get terms() {
    return new CustomerTerms(this.data.terms);
  }

  get attachments() {
    return this.data.attachments.map((doc) => new DocumentRef(doc));
  }

  get items() {
    return this.data.items.map((item, i) => {
      switch (item.itemType) {
        case "discount":
          return new DiscountItem({ ...item, line: i + 1 });
        case "labor":
          return new LaborItem({ ...item, line: i + 1 });
        case "material":
          return new MaterialItem({ ...item, line: i + 1 });
        case "misc":
        default:
          return new MiscItem({ ...item, line: i + 1 });
      }
    });
  }

  get filename() {
    return (
      i18n.global.tc("Quote") + (this.name ? " - " + this.name : "") + ".pdf"
    );
  }

  get status() {
    return new Status(this, this.data.status);
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }
}
